import {
  buildDefaultOrder,
  DatabasesDefaultDirections,
  DatabasesDefaultSortField,
  TablesDefaultDirections,
  TablesDefaultSortField
} from '@tableau/data-catalog-client-components';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { getOrderKeysFromCatalogType, orderByToOrderClauses } from '~src/catalog/fetchers/CatalogOrderClauseHelpers';
import { OrderKeys } from '~src/spacesPlatform/models/OrderKeys';
import { Order } from '~src/spacesPlatform/models/sort/Order';
import { IOrderClause } from '~src/spacesPlatform/serverApi/types/IOrderClause';

import { ContentFilterKeyName, ContentFiltersKeys } from './ContentFilters';

export interface IContentType {
  readonly name: string;
  readonly filterKeys: ContentFilterKeyName[];
  readonly canShowFilterPanel: boolean;
  readonly orderKeys: ReadonlyArray<string>;
  readonly defaultOrder: IOrderClause[];
  readonly extraQueryParams?: any;
}

export interface IContentTypes<T> extends _.Dictionary<T> {
  project: T;
  datasource: T;
  workbook: T;
  view: T;
  dataRole: T;
  lens: T;
  databaseWithCrass: T;
  tableWithCrass: T;
  database: T;
  table: T;
  virtualConnection: T;
  predictiveModel: T;
}

// The "content type" we use when displaying mixed content
export const MixedContentType: IContentType = {
  name: 'content',
  filterKeys: [
    ContentFiltersKeys.search,
    ContentFiltersKeys.owner,
    ContentFiltersKeys.tag, // for recents space only
    ContentFiltersKeys.modifiedAfter,
    ContentFiltersKeys.modifiedBefore,
    ContentFiltersKeys.createdAfter,
    ContentFiltersKeys.createdBefore,
    ContentFiltersKeys.favorite,
  ],
  canShowFilterPanel: true,
  orderKeys: [
    OrderKeys.name,
    OrderKeys.contentTypeOrder,
    OrderKeys.ownerName,
    OrderKeys.updatedAt,
  ],
  defaultOrder: [Order.asc(OrderKeys.contentTypeOrder), Order.asc(OrderKeys.name)]
};

export const DataRoleContentType: IContentType = {
  name: 'dataRole',
  filterKeys: _.compact([
    ContentFiltersKeys.search,
    ContentFiltersKeys.location,
    ContentFiltersKeys.project,
    ContentFiltersKeys.owner,
    ContentFiltersKeys.modifiedAfter,
    ContentFiltersKeys.modifiedBefore,
    ContentFiltersKeys.createdAfter,
    ContentFiltersKeys.createdBefore,
    ContentFiltersKeys.favorite,
  ]),
  canShowFilterPanel: true,
  orderKeys: [
  ],
  defaultOrder: [Order.asc(OrderKeys.name)]
};

export const LensContentType: IContentType = {
  name: 'lens',
  filterKeys: _.compact<_.List<ContentFilterKeyName | ''>>([
    ContentFiltersKeys.search,
    ContentFiltersKeys.location,
    ContentFiltersKeys.project,
    ContentFiltersKeys.owner,
    ContentFiltersKeys.modifiedAfter,
    ContentFiltersKeys.modifiedBefore,
    ContentFiltersKeys.createdAfter,
    ContentFiltersKeys.createdBefore,
    FeatureFlags.askDataLensFavoritingEnabled ? ContentFiltersKeys.favorite : '',
  ]),
  canShowFilterPanel: true,
  orderKeys: [
  ],
  defaultOrder: [Order.asc(OrderKeys.name)]
};

export const WorkbookInDataRoleContentType = _.defaults({
  canShowFilterPanel: false,
  defaultOrder: [Order.asc(OrderKeys.name)]
}, DataRoleContentType);

export const PublishedConnectionContentType: IContentType = {
  name: 'virtualConnection',
  filterKeys: _.compact([
    ContentFiltersKeys.search,
    ContentFiltersKeys.favorite,
    ContentFiltersKeys.project,
    ContentFiltersKeys.owner,
    ContentFiltersKeys.tag,
    ContentFiltersKeys.modifiedAfter,
    ContentFiltersKeys.modifiedBefore
  ]),
  canShowFilterPanel: true,
  orderKeys: [
    OrderKeys.name,
  ],
  defaultOrder: [Order.desc(OrderKeys.relevance)]
};

export const PublishedConnectionTableContentType: IContentType = {
  name: 'virtualConnectionTable',
  filterKeys: [
  ],
  canShowFilterPanel: false,
  orderKeys: [
    OrderKeys.name
  ],
  defaultOrder: [Order.desc(OrderKeys.name)]
};

export const PublishedConnectionColumnContentType: IContentType = {
  name: 'virtualConnectionColumn',
  filterKeys: [
  ],
  canShowFilterPanel: false,
  orderKeys: [
    OrderKeys.name
  ],
  defaultOrder: [Order.desc(OrderKeys.name)]
};

export const PredictiveModelContentType: IContentType = {
  name: 'predictiveModel',
  filterKeys: [
    ContentFiltersKeys.usersPersonalSpaceContent
  ],
  canShowFilterPanel: true,
  orderKeys: [
    OrderKeys.name
  ],
  defaultOrder: [Order.desc(OrderKeys.name)]
};

export const ProjectContentType: IContentType = {
  name: 'project',
  filterKeys: [
    ContentFiltersKeys.search,
    ContentFiltersKeys.owner,
    ContentFiltersKeys.modifiedAfter,
    ContentFiltersKeys.modifiedBefore,
    ContentFiltersKeys.createdAfter,
    ContentFiltersKeys.createdBefore,
    ContentFiltersKeys.allProjects,
    ContentFiltersKeys.favorite,
  ],
  canShowFilterPanel: true,
  orderKeys: [
    OrderKeys.createdAt,
    OrderKeys.datasourceCount,
    OrderKeys.databasesCount,
    OrderKeys.name,
    OrderKeys.ownerName,
    OrderKeys.viewCount,
    OrderKeys.workbookCount,
    OrderKeys.childProjectCount
  ],
  defaultOrder: [Order.asc(OrderKeys.name)]
};

export const WorkbookContentType: IContentType = {
  name: 'workbook',
  filterKeys: [
    ContentFiltersKeys.search,
    ContentFiltersKeys.favorite,
    ContentFiltersKeys.recent,
    ContentFiltersKeys.hasAlert,
    ContentFiltersKeys.location,
    ContentFiltersKeys.project,
    ContentFiltersKeys.owner,
    ContentFiltersKeys.tag,
    ContentFiltersKeys.modifiedAfter,
    ContentFiltersKeys.modifiedBefore,
    ContentFiltersKeys.createdAfter,
    ContentFiltersKeys.createdBefore,
    ContentFiltersKeys.workbookConnFilter,
    ContentFiltersKeys.hasExtracts,
    ContentFiltersKeys.includeLiveToDataEngine,
    ContentFiltersKeys.usersPersonalSpaceContent
  ],
  canShowFilterPanel: true,
  orderKeys: [
    OrderKeys.name,
    OrderKeys.favoritesTotal,
    OrderKeys.hitsTotal,
    OrderKeys.hitsLastOneMonthTotal,
    OrderKeys.hitsLastThreeMonthsTotal,
    OrderKeys.hitsLastTwelveMonthsTotal,
    OrderKeys.numSheets,
    OrderKeys.ownerName,
    OrderKeys.containerName,
    OrderKeys.locationName,
    OrderKeys.relevance,
    OrderKeys.size,
    OrderKeys.subscriptionsTotal,
    OrderKeys.updatedAt,
    OrderKeys.hasAlert
  ],
  extraQueryParams: {
    statFields: [
      OrderKeys.hitsTotal,
      OrderKeys.favoritesTotal,
      OrderKeys.hitsLastOneMonthTotal,
      OrderKeys.hitsLastThreeMonthsTotal,
      OrderKeys.hitsLastTwelveMonthsTotal,
      OrderKeys.subscriptionsTotal
    ]
  },
  defaultOrder: [Order.desc(OrderKeys.hitsTotal),Order.asc(OrderKeys.name)]
};

export const WorkbookInDatasourceContentType = _.defaults({
  canShowFilterPanel: false,
  defaultOrder: [Order.asc(OrderKeys.name)]
}, WorkbookContentType);


export const RevisionContentType: IContentType = {
  name: 'revision',
  filterKeys: [
  ],
  canShowFilterPanel: false,
  orderKeys: [
    OrderKeys.versionNumber
  ],
  defaultOrder: [Order.desc(OrderKeys.versionNumber)]
};

export const ViewStatFields = [
  OrderKeys.hitsTotal,
  OrderKeys.hitsLastOneMonthTotal,
  OrderKeys.hitsLastThreeMonthsTotal,
  OrderKeys.hitsLastTwelveMonthsTotal,
  OrderKeys.favoritesTotal
];

export const ViewContentType: IContentType = {
  name: 'view',
  filterKeys: [
    ContentFiltersKeys.search,
    ContentFiltersKeys.favorite,
    ContentFiltersKeys.recent,
    ContentFiltersKeys.location,
    ContentFiltersKeys.project,
    ContentFiltersKeys.owner,
    ContentFiltersKeys.tag,
    ContentFiltersKeys.modifiedAfter,
    ContentFiltersKeys.modifiedBefore,
    ContentFiltersKeys.createdAfter,
    ContentFiltersKeys.createdBefore,
    ContentFiltersKeys.usersPersonalSpaceContent
  ],
  canShowFilterPanel: true,
  orderKeys: [
    OrderKeys.index,
    OrderKeys.name,
    OrderKeys.favoritesTotal,
    OrderKeys.hitsTotal,
    OrderKeys.hitsLastOneMonthTotal,
    OrderKeys.hitsLastThreeMonthsTotal,
    OrderKeys.hitsLastTwelveMonthsTotal,
    OrderKeys.ownerName,
    OrderKeys.projectName,
    OrderKeys.relevance,
    OrderKeys.updatedAt,
    OrderKeys.workbookName
  ],
  extraQueryParams: {
    statFields: ViewStatFields
  },
  defaultOrder: [Order.desc(OrderKeys.hitsTotal),Order.asc(OrderKeys.name)]
};

export const ViewInWorkbookContentType: IContentType = _.defaults({
  canShowFilterPanel: false,
  defaultOrder: [Order.asc(OrderKeys.index)]
}, ViewContentType);

export const DatasourceContentType: IContentType = {
  name: 'datasource',
  filterKeys: [
    ContentFiltersKeys.search,
    ContentFiltersKeys.hasAlert,
    ContentFiltersKeys.location,
    ContentFiltersKeys.project,
    ContentFiltersKeys.owner,
    ContentFiltersKeys.tag,
    ContentFiltersKeys.modifiedAfter,
    ContentFiltersKeys.modifiedBefore,
    ContentFiltersKeys.createdAfter,
    ContentFiltersKeys.createdBefore,
    ContentFiltersKeys.hasExtracts,
    ContentFiltersKeys.includeLiveToDataEngine,
    ContentFiltersKeys.showEmbedded,
    ContentFiltersKeys.connectionType,
    ContentFiltersKeys.serverName,
    ContentFiltersKeys.serverPort,
    ContentFiltersKeys.databaseUsername,
    ContentFiltersKeys.hasPassword,
    ContentFiltersKeys.favorite,
    ContentFiltersKeys.extractStatus,
    ContentFiltersKeys.isCertified
  ],
  canShowFilterPanel: true,
  orderKeys: [
    OrderKeys.connectionTypeDisplayName,
    OrderKeys.connectedWorkbooksCount,
    OrderKeys.hasExtracts,
    OrderKeys.hasAlert,
    OrderKeys.lastRefreshedAt,
    OrderKeys.name,
    OrderKeys.hitsTotal,
    OrderKeys.hitsLastOneMonthTotal,
    OrderKeys.hitsLastThreeMonthsTotal,
    OrderKeys.hitsLastTwelveMonthsTotal,
    OrderKeys.ownerName,
    OrderKeys.projectName,
    OrderKeys.relevance,
    OrderKeys.updatedAt
  ],
  extraQueryParams: {
    statFields: [
      OrderKeys.hitsTotal,
      OrderKeys.hitsLastOneMonthTotal,
      OrderKeys.hitsLastThreeMonthsTotal,
      OrderKeys.hitsLastTwelveMonthsTotal,
      OrderKeys.connectedWorkbooksCount
    ]
  },
  defaultOrder: [Order.desc(OrderKeys.hitsTotal),Order.asc(OrderKeys.name)]
};

export const DatasourceInWorkbookContentType: IContentType = _.defaults({
  canShowFilterPanel: false,
  defaultOrder: [Order.asc(OrderKeys.name)]
}, DatasourceContentType);

export const DataConnectionContentType: IContentType = {
  name: 'dataConnection',
  filterKeys: DatasourceContentType.filterKeys, // this may be wrong in a world with flows
  canShowFilterPanel: true,
  orderKeys: DatasourceContentType.orderKeys, // this may be wrong in a world with flows
  defaultOrder: [Order.asc(OrderKeys.name)] // data source name; this may be wrong in a world with flows
};

export const DataConnectionInWorkbookContentType: IContentType = _.defaults({
  canShowFilterPanel: false,
  defaultOrder: [Order.asc(OrderKeys.name)]
}, DataConnectionContentType);

export const DataConnectionInDatasourceContentType: IContentType = _.defaults({
  canShowFilterPanel: false,
  defaultOrder: [Order.asc(OrderKeys.connectionTypeDisplayName)]
}, DataConnectionContentType);

export const DataConnectionInFlowContentType: IContentType = _.defaults({
  canShowFilterPanel: false,
  defaultOrder: [Order.asc(OrderKeys.connectionTypeDisplayName)]
}, DataConnectionContentType);

export const FlowContentType: IContentType = {
  name: 'flow',
  filterKeys: _.compact([
    ContentFiltersKeys.search,
    ContentFiltersKeys.favorite,
    ContentFiltersKeys.location,
    ContentFiltersKeys.project,
    ContentFiltersKeys.owner,
    ContentFiltersKeys.tag,
    ContentFiltersKeys.modifiedAfter,
    ContentFiltersKeys.modifiedBefore
  ]),
  canShowFilterPanel: true,
  orderKeys: _.compact([
    OrderKeys.name,
    OrderKeys.favoritesTotal,
    OrderKeys.ownerName,
    OrderKeys.projectName,
    OrderKeys.updatedAt,
  ]),
  defaultOrder: [Order.asc(OrderKeys.name)]
};

export const FlowOutputStepRunContentType: IContentType = {
  name: 'flowOutputStepRun',
  filterKeys: [
    ContentFiltersKeys.flowOutputStep,
    ContentFiltersKeys.status,
    ContentFiltersKeys.startedAfter,
    ContentFiltersKeys.startedBefore,
    ContentFiltersKeys.completedAfter,
    ContentFiltersKeys.completedBefore,
  ],
  canShowFilterPanel: true,
  orderKeys: [
    OrderKeys.startedAt,
    OrderKeys.completedAt,
    OrderKeys.status,
    OrderKeys.rowsProduced,
    OrderKeys.flowOutputStepName,
    OrderKeys.publishedDataSourceName,
  ],
  defaultOrder: [Order.desc(OrderKeys.startedAt)]
};

export const DatabaseWithCRASSSearchSubspaceType: IContentType = {
  name: 'databaseWithCrass',
  filterKeys: _.compact<_.List<ContentFilterKeyName | ''>>([
    ContentFiltersKeys.search,
    (FeatureFlags.databasesInProjectsEnabled && FeatureFlags.databasesInProjectsPostMVPEnabled) ? ContentFiltersKeys.location : '',
    ContentFiltersKeys.hasActiveDataQualityWarning,
    ContentFiltersKeys.isCertified,
    ContentFiltersKeys.tag,
    ContentFiltersKeys.isEmbedded,
  ]),
  canShowFilterPanel: true,
  orderKeys: [
    OrderKeys.connectionTypeDisplayNameCrass,
    OrderKeys.downstreamWorkbookCount,
    OrderKeys.downstreamDataSourceCount,
    OrderKeys.isCertified,
    OrderKeys.name,
  ],
  defaultOrder: [Order.desc(OrderKeys.relevance)],
};

export const DatabaseWithCRASSContentType: IContentType = {
  name: 'databaseWithCrass',
  filterKeys: _.compact<_.List<ContentFilterKeyName | ''>>([
    ContentFiltersKeys.search,
    (FeatureFlags.databasesInProjectsEnabled && FeatureFlags.databasesInProjectsPostMVPEnabled) ? ContentFiltersKeys.location : '',
    ContentFiltersKeys.hasActiveDataQualityWarning,
    ContentFiltersKeys.isCertified,
    ContentFiltersKeys.tag,
    ContentFiltersKeys.isEmbedded,
  ]),
  canShowFilterPanel: true,
  orderKeys: [
    OrderKeys.connectionTypeDisplayNameCrass,
    OrderKeys.downstreamWorkbookCount,
    OrderKeys.downstreamDataSourceCount,
    OrderKeys.isCertified,
    OrderKeys.name,
  ],
  defaultOrder: [Order.desc(OrderKeys.downstreamWorkbookCount)],
};

// At the time of ContentType.ts initialziation happens at the begining and FeatureFlags cannot be overwritten using
// Launch Darkly. So, need to add location filterkey at the time of tablesInProjectsEnabled graudation
export const TableWithCRASSSearchSubspaceType: IContentType = {
  name: 'tableWithCrass',
  filterKeys: [
    ContentFiltersKeys.search,
    ContentFiltersKeys.hasActiveDataQualityWarning,
    ContentFiltersKeys.isCertified,
    ContentFiltersKeys.tag,
    ContentFiltersKeys.isEmbedded,
  ],
  canShowFilterPanel: true,
  orderKeys: [
    OrderKeys.downstreamWorkbookCount,
    OrderKeys.downstreamDataSourceCount,
    OrderKeys.isCertified,
    OrderKeys.name,
  ],
  defaultOrder: [Order.desc(OrderKeys.relevance)],
};

// At the time of ContentType.ts initialziation happens at the begining and FeatureFlags cannot be overwritten using
// Launch Darkly. So, need to add location filterkey at the time of tablesInProjectsEnabled graudation
export const TableWithCRASSContentType: IContentType = {
  name: 'tableWithCrass',
  filterKeys: [
    ContentFiltersKeys.search,
    ContentFiltersKeys.hasActiveDataQualityWarning,
    ContentFiltersKeys.isCertified,
    ContentFiltersKeys.tag,
    ContentFiltersKeys.isEmbedded,
  ],
  canShowFilterPanel: true,
  orderKeys: [
    OrderKeys.downstreamWorkbookCount,
    OrderKeys.downstreamDataSourceCount,
    OrderKeys.isCertified,
    OrderKeys.name,
  ],
  defaultOrder: [Order.desc(OrderKeys.downstreamWorkbookCount)],
};

export const DatabaseContentType: IContentType = {
    name: 'database',
    filterKeys: [
      ContentFiltersKeys.search,
      ContentFiltersKeys.hasActiveWarning,
      ContentFiltersKeys.isCertified,
      ContentFiltersKeys.tag,
    ],
    canShowFilterPanel: true,
    orderKeys: getOrderKeysFromCatalogType(DatabasesDefaultDirections),
    defaultOrder: orderByToOrderClauses(buildDefaultOrder(DatabasesDefaultSortField, DatabasesDefaultDirections)),
  };

export const TableContentType: IContentType = {
  name: 'table',
  filterKeys: [
    ContentFiltersKeys.search,
    ContentFiltersKeys.hasActiveWarning,
    ContentFiltersKeys.isCertified,
    ContentFiltersKeys.tag,
  ],
  canShowFilterPanel: true,
  orderKeys: getOrderKeysFromCatalogType(TablesDefaultDirections),
  defaultOrder: orderByToOrderClauses(buildDefaultOrder(TablesDefaultSortField, TablesDefaultDirections)),
};

export const SitesContentType: IContentType = {
  name: 'sites',
  filterKeys: [
    ContentFiltersKeys.search
  ],
  canShowFilterPanel: true,
  orderKeys: [
    OrderKeys.adminCount,
    OrderKeys.authoringEnabled,
    OrderKeys.availability,
    OrderKeys.createdAt,
    OrderKeys.currentLoggedInUserCount,
    OrderKeys.extractEncryptionMode,
    OrderKeys.guestAccessEnabled,
    OrderKeys.metricsEnabled,
    OrderKeys.name,
    OrderKeys.sheetImageEnabled,
    OrderKeys.siteAdminsCanAddUsers,
    OrderKeys.storageUsedBytes,
    OrderKeys.storageQuotaBytes,
    OrderKeys.storageQuotaPercentUsed,
    OrderKeys.urlName,
    OrderKeys.userCount,
    OrderKeys.userQuota
  ],
  defaultOrder: [Order.asc(OrderKeys.name)]
};

export const GroupContentType: IContentType = {
  name: 'group',
  filterKeys: [
    ContentFiltersKeys.search,
    ContentFiltersKeys.domain,
    ContentFiltersKeys.groupsMinimumSiteRole
  ],
  canShowFilterPanel: true,
  orderKeys: [
    OrderKeys.name,
    OrderKeys.domainName,
    OrderKeys.userCount,
    OrderKeys.minimumSiteRole
  ],
  defaultOrder: [Order.asc(OrderKeys.name)]
};

export const BackgroundJobContentType: IContentType = {
  name: 'backgroundJob',
  filterKeys: [
    ContentFiltersKeys.backgroundJobStatus,
    ContentFiltersKeys.backgroundJobTaskType,
    ContentFiltersKeys.backgroundJobTimeRange,
    ContentFiltersKeys.showSystemJobs,
  ],
  canShowFilterPanel: true,
  orderKeys: [
    OrderKeys.jobId,
    OrderKeys.status,
    OrderKeys.priority,
    OrderKeys.taskType,
    OrderKeys.currentRunTime,
    OrderKeys.currentQueueTime,
    OrderKeys.jobRequestedTime,
  ],
  defaultOrder: [Order.desc(OrderKeys.jobRequestedTime)]
};


export const SiteUserContentType: IContentType = {
  name: 'siteUser',
  filterKeys: [
    ContentFiltersKeys.search,
    ContentFiltersKeys.siteRole,
    ContentFiltersKeys.isTabID
  ],
  canShowFilterPanel: true,
  orderKeys: [
    OrderKeys.displayName,
    OrderKeys.domainName,
    OrderKeys.groupCount,
    OrderKeys.lastSignIn,
    OrderKeys.siteRole,
    OrderKeys.username
  ],
  defaultOrder: [Order.asc(OrderKeys.displayName)]
};

export const ScheduleContentType: IContentType = {
  name: 'schedule',
  filterKeys: [
  ],
  canShowFilterPanel: false,
  orderKeys: [
    OrderKeys.name,
    'scheduleType',
    'scheduledAction',
    'taskCount',
    'parallel',
    'runNextAt'
  ],
  defaultOrder: [Order.asc(OrderKeys.name)]
};

export const AccelerationTaskContentType: IContentType = {
  name: 'accelerationTask',
  filterKeys: [
    ContentFiltersKeys.accelerationStatus,
  ],
  canShowFilterPanel: true,
  orderKeys: [
    OrderKeys.accelerationStatus,
    OrderKeys.targetName,
    OrderKeys.ownerName,
    OrderKeys.viewCount,
    OrderKeys.workbookName,
  ],
  defaultOrder: [Order.asc(OrderKeys.targetName)]
};

export const ExtractTaskContentType: IContentType = {
  name: 'extractTask',
  filterKeys: [
  ],
  canShowFilterPanel: false,
  orderKeys: [
    OrderKeys.type,
    OrderKeys.targetName,
    OrderKeys.targetType,
    OrderKeys.scheduleName,
    OrderKeys.priority,
    OrderKeys.lastRunAt,
    OrderKeys.runNextAt,
    OrderKeys.siteName
  ],
  defaultOrder: [Order.asc(OrderKeys.targetName)]
};

export const ResourceGovernanceSettingContentType: IContentType = {
  name: 'resourceGovernanceSetting',
  filterKeys: [],
  canShowFilterPanel: false,
  orderKeys: [OrderKeys.siteName],
  extraQueryParams: {},
  defaultOrder: [Order.asc(OrderKeys.siteName)],
};

export const ExtractTaskSingleTargetContentType: IContentType = {
  name: 'extractTask',
  filterKeys: [
  ],
  canShowFilterPanel: false,
  orderKeys: [
    OrderKeys.scheduleName,
    OrderKeys.priority,
    OrderKeys.lastRunAt,
    OrderKeys.runNextAt,
    OrderKeys.siteName
  ],
  defaultOrder: [Order.asc(OrderKeys.scheduleName)]
};

export const RemoteRefreshScheduleContentType: IContentType = {
  name: 'remoteRefreshSchedule',
  filterKeys: [
  ],
  canShowFilterPanel: false,
  orderKeys: [
    OrderKeys.refreshType,
    OrderKeys.definition,
    OrderKeys.lastRunAt,
    OrderKeys.runNextAt
  ],
  defaultOrder: [Order.asc(OrderKeys.runNextAt)]
};

export const CustomViewsContentType: IContentType = {
  name: 'customView',
  filterKeys: [],
  canShowFilterPanel: false,
  orderKeys: [
    // sortable options coming from CustomViewSortOptions
  ],
  extraQueryParams: {
    statFields: [
      OrderKeys.hitsTotal,
      OrderKeys.hitsLastOneMonthTotal,
      OrderKeys.hitsLastThreeMonthsTotal,
      OrderKeys.hitsLastTwelveMonthsTotal,
    ]
  },
  defaultOrder: [Order.asc(OrderKeys.name)]
};

export const SubscriptionContentType: IContentType = {
  name: 'subscription',
  filterKeys: [
  ],
  canShowFilterPanel: false,
  orderKeys: [
    OrderKeys.targetName,
    OrderKeys.userDisplayName,
    OrderKeys.subject,
    OrderKeys.scheduleName,
    OrderKeys.lastSentAt,
    OrderKeys.runNextAt,
    OrderKeys.siteName,
    OrderKeys.creatorName
  ],
  defaultOrder: [Order.asc(OrderKeys.subject)]
};

export const ServerUserContentType: IContentType = {
  name: 'serverUser',
  filterKeys: [
    ContentFiltersKeys.search,
    ContentFiltersKeys.maxSiteRole
  ],
  canShowFilterPanel: true,
  orderKeys: [
    OrderKeys.displayName,
    OrderKeys.domainName,
    OrderKeys.siteCount,
    OrderKeys.lastSignIn,
    OrderKeys.maxSiteRole,
    OrderKeys.licenseLevel,
    OrderKeys.username
  ],
  defaultOrder: [Order.asc(OrderKeys.displayName)]
};

export const UserIdentityConflictContentType: IContentType = {
  name: 'userIdentityConflict',
  filterKeys: [
    ContentFiltersKeys.discardIdentity,
    ContentFiltersKeys.failureType,
    ContentFiltersKeys.search,
  ],
  canShowFilterPanel: false,
  orderKeys: [
  ],
  defaultOrder: []
};

export const GroupUserContentType: IContentType = {
  name: 'groupUser',
  filterKeys: [
  ],
  canShowFilterPanel: false,
  orderKeys: [
    OrderKeys.displayName,
    OrderKeys.siteCount,
    OrderKeys.lastSignIn,
    OrderKeys.siteRole,
    OrderKeys.username
  ],
  defaultOrder: [Order.asc(OrderKeys.displayName)]
};

export const DataAlertContentType: IContentType = {
  name: 'dataAlert',
  filterKeys: [],
  canShowFilterPanel: false,
  orderKeys: [
    OrderKeys.title,
    OrderKeys.targetName,
    OrderKeys.ownerName,
    OrderKeys.notificationIntervalInMinutes,
    OrderKeys.lastChecked
  ],
  defaultOrder: [Order.asc(OrderKeys.title)]
};

export const RunFlowTaskContentType: IContentType = {
  name: 'runFlowTask',
  filterKeys: [],
  canShowFilterPanel: false,
  orderKeys: [
    // TODO: enable sorting by flowName on backend
    //OrderKeys.flowName,
    OrderKeys.scheduleName,
    OrderKeys.priority,
    // TODO: enable sort by lastRunAt on backend
    // OrderKeys.lastRunAt,
    OrderKeys.runNextAt,
    OrderKeys.siteName,
  ],
  // TODO: change defaultOrder to flowName when enabled on BE
  defaultOrder: [Order.asc(OrderKeys.scheduleName)]
};
